import { useMutation } from '@apollo/client/react'
import { FailureNotice, FieldMessage, useLanguage, useTranslate } from '@athena/ui'
import { useNavigate } from '@tanstack/react-router'
import { useRef, useState, type ReactNode } from 'react'

import { invalidateStudentActivity } from '../attempt/cache.js'
import { StartAttemptDocument } from '../../graphql/generated/operations.js'
import { useFirstUserErrorText } from '../../lib/user-errors.js'

import { TimedStartDialog } from './TimedStartDialog.js'

/** What starting a practice needs to know about it. */
export interface Startable {
  practiceId: string
  title: string
  timed: boolean
  durationMinutes: number | null
  /** Whether the student has one open already, which a timed practice resumes without asking. */
  inProgress: boolean
}

export interface StartPractice {
  /** Start it, or ask first when it is timed and not yet begun. */
  requestStart: (practice: Startable) => void
  /** The practice being opened, so only its own button says "Opening…". */
  startingId: string | null
  /** What went wrong with the last open, if anything, to render where the student is looking. */
  feedback: ReactNode
  /** The timed-practice rules, stated before the clock starts. Render it once. */
  dialog: ReactNode
}

/**
 * Opening a practice from anywhere a student can: the home's cards and the
 * progress screen's "Try again".
 *
 * One flow, because every rule about starting is the same wherever the button
 * is — a timed practice states its rules before the clock starts, a second
 * press waits for the first, a refusal is said in the server's words, and a
 * request that never got an answer offers to open the same one again. Which
 * practices can be started is still the server's answer (`canStart`); this
 * only carries it out.
 */
export function useStartPractice(): StartPractice {
  const t = useTranslate()
  const userErrorText = useFirstUserErrorText()
  const language = useLanguage()
  const navigate = useNavigate()

  const [startAttempt] = useMutation(StartAttemptDocument, {
    update(cache, { data }) {
      if (data?.startAttempt.attempt) invalidateStudentActivity(cache)
    },
  })
  const [startError, setStartError] = useState<string>()
  /** An open that never got an answer, and which practice it was for, so "Try again" opens the same one. */
  const [startFailure, setStartFailure] = useState<{ practiceId: string; error: unknown }>()
  /**
   * The practice being opened, so only its own button says "Opening…" and a
   * second press of any card waits for this one. Opening twice is harmless —
   * the server resumes the attempt the first one started, even for a timed
   * practice — but a student who pressed once should not see every card busy.
   */
  const [startingId, setStartingId] = useState<string | null>(null)
  const opening = useRef(false)
  /**
   * The timed practice waiting on the student's confirmation, or null.
   *
   * Opening a timed practice does not start it. The mutation runs only when
   * the student presses Start in the dialog, which is what makes the clock
   * something they chose to begin rather than something that had already been
   * running when they read the rules.
   */
  const [confirming, setConfirming] = useState<Startable | null>(null)

  async function open(practiceId: string) {
    if (opening.current) return
    opening.current = true
    setStartingId(practiceId)
    setStartError(undefined)
    let payload
    try {
      payload = (await startAttempt({ variables: { practiceId } })).data?.startAttempt
    } catch (error) {
      setStartFailure({ practiceId, error })
      return
    } finally {
      opening.current = false
      setStartingId(null)
    }
    setStartFailure(undefined)

    if (payload?.attempt) {
      await navigate({
        to: '/$language/learn/attempt/$attemptId',
        params: { language, attemptId: payload.attempt.id },
      })
      return
    }
    setStartError(userErrorText(payload?.userErrors, t('learn.startFailed')))
  }

  /**
   * Start, or ask first.
   *
   * A timed practice that has not been opened yet gets the dialog. Resuming
   * one does not: the clock has been running since the first start, and asking
   * again would imply it had stopped.
   */
  function requestStart(practice: Startable) {
    if (practice.timed && !practice.inProgress) {
      setConfirming(practice)
      return
    }
    void open(practice.practiceId)
  }

  const feedback = (
    <>
      {startError ? <FieldMessage tone="error">{startError}</FieldMessage> : null}
      {startFailure ? (
        <FailureNotice
          title={t('learn.startFailed')}
          error={startFailure.error}
          retrying={startingId === startFailure.practiceId}
          onRetry={() => void open(startFailure.practiceId)}
        />
      ) : null}
    </>
  )

  const dialog = (
    <TimedStartDialog
      practice={confirming}
      onOpenChange={(isOpen) => {
        if (!isOpen) setConfirming(null)
      }}
      onConfirm={() => {
        const practice = confirming
        setConfirming(null)
        if (practice) void open(practice.practiceId)
      }}
    />
  )

  return { requestStart, startingId, feedback, dialog }
}
